import * as Sentry from '@sentry/react';
import { sleep } from './utils';

const MAX_ATTEMPTS = 3;
const BASE_RETRY_DELAY_MS = 300;

export interface SessionTokenRequestContext {
    /** HTTP method of the request the token is being fetched for (for Sentry tagging). */
    method?: string;
    /** Request path (without base URL) the token is being fetched for (for Sentry tagging). */
    path?: string;
}

/**
 * Thrown when a Shopify session token could not be obtained after all retry attempts.
 * The request is aborted rather than sent with a missing/`undefined` bearer token.
 */
export class ShopifySessionTokenError extends Error {
    constructor(
        public readonly attempts: number,
        public readonly cause?: unknown
    ) {
        super(`Failed to fetch Shopify session token after ${attempts} attempt(s)`);
        this.name = 'ShopifySessionTokenError';
    }
}

const fetchIdToken = async (): Promise<string> => {
    if (typeof shopify === 'undefined' || typeof shopify.idToken !== 'function') {
        throw new Error('App Bridge `shopify.idToken` is not available');
    }
    const token = await shopify.idToken();
    if (typeof token !== 'string' || token.length === 0) {
        throw new Error(`shopify.idToken() resolved with an empty token (${typeof token})`);
    }
    return token;
};

/**
 * Fetches a Shopify session token via App Bridge, retrying on rejection or on an empty token.
 * After the last failed attempt the failure is reported to Sentry and a
 * {@link ShopifySessionTokenError} is thrown.
 */
export const getShopifySessionToken = async (context: SessionTokenRequestContext = {}): Promise<string> => {
    let lastError: unknown;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
            return await fetchIdToken();
        } catch (error) {
            lastError = error;
            if (attempt < MAX_ATTEMPTS) {
                await sleep(BASE_RETRY_DELAY_MS * attempt);
            }
        }
    }

    const tokenError = new ShopifySessionTokenError(MAX_ATTEMPTS, lastError);
    Sentry.captureException(tokenError, {
        tags: {
            'session_token.method': context.method ?? 'unknown',
            'session_token.path': context.path ?? 'unknown',
        },
        extra: {
            attempts: MAX_ATTEMPTS,
            lastError: lastError instanceof Error ? lastError.message : String(lastError),
            appBridgeAvailable: typeof shopify !== 'undefined',
        },
    });
    throw tokenError;
};
